import React, { createContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { paramaterize } from 'utils';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { TabType, TabMenu, orgType as OrgType } from './types';
import getMlsTypeFilterOptions from '../../../api/AnsweringOrgController/getMlsTypeFilterOptions';
import { MLS_TYPE_LABEL_MAP } from '../../../components/UniversalSearch/constants';
import hasValidStripeMlsProductId from '../../../api/AnsweringOrgController/hasValidStripeMlsProductId';

type AnsweringSearchContextType = {
  selectedTab: TabMenu;
  text: string;
  page: number;
  total: number;
  initialTotal: number;
  isLoading: boolean;
  orgType: OrgType;
  mlsFilterOptions: { text: string; value: string }[];
  csvLink: string;
  isMls: boolean;
  setSelectedTab: React.Dispatch<React.SetStateAction<TabMenu>>;
  setText: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  setInitialTotal: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  generateCsvDownload: (options: {
    input: string;
    sortField: string;
    sortDir: string;
    dataType: 'brokerage' | 'agent';
    from?: number;
    page?: number;
    answeringSubscribed?: boolean;
    plans?: string[];
    statuses?: string[];
  }) => void;
  hasValidStripeMlsProductIdForOrg: boolean;
};

type AnsweringSearchContextProviderProps = {
  tab: TabType;
  orgType: OrgType;
};

const AnsweringSearchContext = createContext<AnsweringSearchContextType>(null);

export const AnsweringSearchContextProvider: React.FC<
  AnsweringSearchContextProviderProps
> = ({ children, tab, orgType }) => {
  const { page: initialPage } = useParams<{ page?: string }>();
  const history = useHistory();
  const [selectedTab, _setSelectedTab] = React.useState<TabMenu>(
    (tab === 'brokerages' && TabMenu.Brokerages) || TabMenu.Agents,
  );
  const [text, setText] = React.useState('');
  const [page, setPage] = React.useState(parseInt(initialPage ?? '0', 10));
  const [mlsFilterOptions, setMlsFilterOptions] = React.useState<
    { text: string; value: string }[]
  >([]);
  const prevSearchParam = React.useRef<string>('');
  const [total, setTotal] = React.useState(0);
  const [initialTotal, setInitialTotal] = React.useState(0);
  const [csvLink, setCsvLink] = React.useState('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [
    hasValidStripeMlsProductIdForOrg,
    setHasValidStripeMlsProductIdForOrg,
  ] = React.useState<boolean>(false);

  const generateCsvDownload = ({
    from,
    input,
    sortField,
    sortDir,
    dataType,
    answeringSubscribed,
    answeringServiceTypes,
    plans,
    statuses,
  }: {
    from: number;
    input: string;
    sortField: string;
    sortDir: string;
    dataType: 'brokerage' | 'agent';
    answeringSubscribed?: boolean;
    answeringServiceTypes?: (AnsweringServiceType | 'ns' | 'on')[];
    plans?: string[];
    statuses?: string[];
  }) => {
    let base = `/answering/export_csv?${paramaterize({
      dataType,
      input,
      sortField,
      sortDir,
    })}`;
    if (typeof answeringSubscribed === 'boolean') {
      base += `&answeringSubscribed=${answeringSubscribed}`;
    }
    if (typeof from === 'number') {
      base += `&from=${from}`;
    }
    if (typeof page === 'number') {
      base += `&page=${page}`;
    }
    if (answeringServiceTypes) {
      answeringServiceTypes.forEach(ast => {
        base += `&answeringServiceTypes[]=${ast}`;
      });
    }
    if (dataType === 'brokerage') {
      if (plans) {
        plans.forEach(plan => {
          base += `&plans[]=${plan}`;
        });
      }
      if (statuses) {
        statuses.forEach(status => {
          base += `&statuses[]=${status}`;
        });
      }
    }
    setCsvLink(base);
  };

  React.useEffect(() => {
    history.replace({
      ...history.location,
      pathname: `/answering/search/${selectedTab.toLowerCase()}/${page}`,
    });
  }, [history, page, selectedTab]);

  React.useEffect(() => {
    if (orgType === 'answering') {
      (async () => {
        const buildMlsOptions = (options: string[]) => {
          const databaseOptions = options.reduce((acc, curr) => {
            acc.push({
              text: MLS_TYPE_LABEL_MAP[curr] || curr,
              value: curr,
            });
            return acc;
          }, []);
          return [...databaseOptions];
        };
        const mlsTypeFilterOptions = await getMlsTypeFilterOptions();
        setMlsFilterOptions(buildMlsOptions(mlsTypeFilterOptions));
      })();
    }
    (async () => {
      setHasValidStripeMlsProductIdForOrg(await hasValidStripeMlsProductId());
    })();
  }, [orgType]);

  const setSelectedTab = (newTab: TabMenu) => {
    if (newTab === selectedTab) return;
    const prevSearch = prevSearchParam.current;
    prevSearchParam.current = history.location.search;
    if (prevSearch) {
      history.replace({ search: prevSearch });
    } else {
      history.replace({ search: '' });
    }
    _setSelectedTab(newTab);
  };

  return (
    <AnsweringSearchContext.Provider
      value={{
        selectedTab,
        text,
        mlsFilterOptions,
        page,
        total,
        initialTotal,
        isMls: orgType === 'mlsStaff',
        setInitialTotal,
        setSelectedTab,
        setText,
        setPage,
        setTotal,
        orgType,
        csvLink,
        generateCsvDownload,
        isLoading,
        setIsLoading,
        hasValidStripeMlsProductIdForOrg,
      }}
    >
      {children}
    </AnsweringSearchContext.Provider>
  );
};

export default AnsweringSearchContext;
